import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { FilterBar } from '@/components/common/FilterBar';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { loginActivityApi } from '@/api/services/loginActivity';
import type { LoginSessionDto } from '@/api/types/loginActivity';

// App-standard DD/MM/YYYY HH:mm:ss (not the browser's US-locale format).
function formatDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function today(): string {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

interface DateFilter {
  begDate?: string;
  endDate?: string;
}

// Host "Login Activity": login/logout sessions across every tenant, filtered by
// a From/To date range. Legacy defaults both pickers to today and only reloads
// the table when "Fetch Data" is clicked — reproduced with draft vs applied state.
export function LoginActivityPage() {
  const [draftFrom, setDraftFrom] = useState<string>(today());
  const [draftTo, setDraftTo] = useState<string>(today());
  const [applied, setApplied] = useState<DateFilter>({ begDate: today(), endDate: today() });

  const extraParams = useMemo<DateFilter>(
    () => ({ begDate: applied.begDate, endDate: applied.endDate }),
    [applied],
  );

  const table = useServerDataTable<LoginSessionDto, DateFilter>({
    queryKey: ['loginActivity', 'list'],
    fetcher: (req) => loginActivityApi.getListForDataTable(req),
    extraParams,
    pageSize: 5,
  });

  const columns: DataGridColumn<LoginSessionDto>[] = [
    { key: 'tenantName', header: 'Client', sortable: true, filterable: true, cell: (r) => r.tenantName },
    { key: 'userName', header: 'User Name', sortable: true, filterable: true, cell: (r) => r.userName },
    { key: 'clientIpAddress', header: 'Ip', sortable: true, cell: (r) => r.clientIpAddress },
    {
      key: 'loginTime',
      header: 'Login Time',
      sortable: true,
      cell: (r) => formatDateTime(r.loginTime),
    },
    {
      key: 'logoutTime',
      header: 'Logout Time',
      sortable: true,
      cell: (r) => formatDateTime(r.logoutTime),
    },
    {
      key: 'sessionDuration',
      header: 'Session Duration',
      sortable: true,
      cell: (r) => r.sessionDuration,
    },
    { key: 'logoutType', header: 'Logout Type', sortable: true, cell: (r) => r.logoutType },
  ];

  function fetchData() {
    setApplied({ begDate: draftFrom || undefined, endDate: draftTo || undefined });
  }

  return (
    <div>
      <PageHeader title="Login Activity" breadcrumb={['Home', 'Login Activity']} />

      <FilterBar
        fields={[
          {
            node: (
              <div>
                <label className="form-label">From</label>
                <DateRangePicker
                  mode="single"
                  maxDate={today()}
                  value={draftFrom || null}
                  onChange={(v) => setDraftFrom(v)}
                />
              </div>
            ),
            width: 240,
            pinned: true,
          },
          {
            node: (
              <div>
                <label className="form-label">To</label>
                <DateRangePicker
                  mode="single"
                  maxDate={today()}
                  value={draftTo || null}
                  onChange={(v) => setDraftTo(v)}
                />
              </div>
            ),
            width: 240,
            pinned: true,
          },
        ]}
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={fetchData}>
            <i className="fa fa-search me-2" />
            Fetch Data
          </button>
        }
      />

      <div className="card">
        <div className="card-body">
          <DataGrid
            controller={table}
            columns={columns}
            rowKey={(r) => r.id}
            emptyMessage="No login activity found."
            toolbarEnd={
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                title="Reload Table"
                onClick={() => table.refetch()}
              >
                <i className="fa fa-refresh" />
              </button>
            }
          />
        </div>
      </div>
    </div>
  );
}
